import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Link,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { AutoAwesome, Check, Close, Refresh } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import executionService from "../services/executionService";
import { elapsedMs } from "../utils/dateUtils";

// A failed run's analysis (backend: FailureAnalyzer): Testify's fixed checks,
// then the AI, recommend changes to the test. Nothing changes until someone who
// may edit the test clicks Apply.

const POLL_MS = 3000;
// The backend marks a failed run's analysis PENDING before it's queued, so
// "none" right after a failure is rare (the result PUT still in flight). Keep
// asking this long after the run failed; an older run won't get one by itself.
const JUST_FAILED_MS = 3 * 60 * 1000;
// A load error (network blip, backend restarting) is retried this many times in a row
const MAX_ERRORS = 5;

function actionLabel(rec) {
	const step = rec.stepNumber ? `step ${rec.stepNumber}` : null;
	switch (rec.action) {
		case "DELETE_STEP":
			return `Delete ${step}`;
		case "DISABLE_STEP":
			return `Turn off ${step}`;
		case "SET_VALUE":
			return `Set ${step}'s value to "${rec.value}"`;
		case "INSERT_WAIT":
			return `Add a 1-second wait before ${step}`;
		default:
			return step ? `Advice about ${step}` : "Advice";
	}
}

const APPLIED_MESSAGE = {
	DELETE_STEP: (n) => `Step ${n} was deleted from the test`,
	DISABLE_STEP: (n) => `Step ${n} was turned off`,
	SET_VALUE: (n) => `Step ${n}'s value was changed`,
	INSERT_WAIT: (n) => `A wait was added before step ${n}`,
};

export default function FailureAnalysisPanel({ executionId, testId, completedAt }) {
	const { enqueueSnackbar } = useSnackbar();
	const [analysis, setAnalysis] = useState(null);
	const [loaded, setLoaded] = useState(false);
	// Still expecting a result: analysis PENDING, or not started yet
	const [waiting, setWaiting] = useState(true);
	const [busyId, setBusyId] = useState(null);
	const [requesting, setRequesting] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);
	const startedAt = useRef(Date.now());

	useEffect(() => {
		let cancelled = false;
		let timer = null;
		let errors = 0;
		startedAt.current = Date.now();
		setAnalysis(null);
		setLoaded(false);
		setWaiting(true);
		setLoadFailed(false);

		// Without a known end time, count from when the panel appeared
		const justFailed = () => {
			const since = completedAt
				? elapsedMs(completedAt, Date.now())
				: Date.now() - startedAt.current;
			return since == null || since < JUST_FAILED_MS;
		};

		const load = async () => {
			try {
				const latest = await executionService.getAnalysis(executionId);
				if (cancelled) return;
				errors = 0;
				setAnalysis(latest);
				setLoaded(true);
				// PENDING: the backend ends one that died (restart) after a few minutes
				const stillWaiting = latest?.status === "PENDING" || (!latest && justFailed());
				setWaiting(stillWaiting);
				if (stillWaiting) timer = setTimeout(load, POLL_MS);
			} catch {
				if (cancelled) return;
				errors += 1;
				if (errors < MAX_ERRORS) {
					timer = setTimeout(load, POLL_MS);
				} else {
					setLoaded(true);
					setWaiting(false);
					setLoadFailed(true);
				}
			}
		};
		load();
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [executionId, requesting]);

	const errorText = (error, fallback) =>
		error.response?.data?.message || fallback;

	const handleRequest = async () => {
		try {
			setAnalysis(await executionService.requestAnalysis(executionId));
			setRequesting((r) => !r); // restarts the polling effect
		} catch (error) {
			enqueueSnackbar(errorText(error, "Couldn't start the analysis"), { variant: "error" });
		}
	};

	const handleResolve = async (rec, apply) => {
		setBusyId(rec.id);
		try {
			const updated = apply
				? await executionService.applyRecommendation(executionId, rec.id)
				: await executionService.dismissRecommendation(executionId, rec.id);
			setAnalysis(updated);
			if (apply) {
				enqueueSnackbar(
					`${APPLIED_MESSAGE[rec.action]?.(rec.stepNumber) || "The test was changed"}. Run it again to check.`,
					{ variant: "success" },
				);
			}
		} catch (error) {
			enqueueSnackbar(
				errorText(error, apply ? "Couldn't change the test" : "Couldn't dismiss it"),
				{ variant: "error" },
			);
		} finally {
			setBusyId(null);
		}
	};

	const pending = !loaded || waiting;

	return (
		<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
				<AutoAwesome fontSize="small" color="primary" />
				<Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
					Failure analysis
				</Typography>
				{analysis?.aiUsed && <Chip size="small" label="AI" color="primary" variant="outlined" />}
				{!pending && !loadFailed && (
					<Button size="small" startIcon={<Refresh />} onClick={handleRequest}>
						{analysis ? "Analyze again" : "Analyze failure"}
					</Button>
				)}
			</Stack>

			{pending ? (
				<Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
					<CircularProgress size={18} />
					<Typography variant="body2" color="text.secondary">
						Looking for the cause and how to fix the test…
					</Typography>
				</Stack>
			) : loadFailed ? (
				<Stack direction="row" alignItems="center" spacing={1}>
					<Typography variant="body2" color="text.secondary">
						Couldn't load the analysis.
					</Typography>
					<Button size="small" onClick={() => setRequesting((r) => !r)}>
						Retry
					</Button>
				</Stack>
			) : !analysis ? (
				<Typography variant="body2" color="text.secondary">
					This run hasn't been analysed.
				</Typography>
			) : analysis.status === "UNAVAILABLE" ? (
				<Typography variant="body2" color="text.secondary">
					No likely cause was found in the test's steps
					{analysis.aiUsed === false ? " (AI analysis is off or didn't answer)" : ""}.
				</Typography>
			) : (
				<>
					{analysis.summary && (
						<Typography variant="body2" sx={{ mb: 1.5 }}>
							{analysis.summary}
						</Typography>
					)}
					{analysis.recommendations?.length === 0 && (
						<Typography variant="body2" color="text.secondary">
							No change to the test is recommended.
						</Typography>
					)}
					<Stack spacing={1}>
						{analysis.recommendations?.map((rec) => {
							const open = rec.status === "OPEN";
							const applicable = rec.action !== "ADVICE";
							return (
								<Box
									key={rec.id}
									sx={{
										p: 1.5,
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
										bgcolor: open ? "background.paper" : "action.hover",
									}}
								>
									<Stack
										direction={{ xs: "column", sm: "row" }}
										alignItems={{ xs: "flex-start", sm: "center" }}
										spacing={1}
										sx={{ mb: 0.5 }}
									>
										<Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
											{actionLabel(rec)}
										</Typography>
										<Stack direction="row" spacing={0.5}>
											<Chip
												size="small"
												variant="outlined"
												label={rec.source === "rule" ? "Testify check" : "AI"}
											/>
											{rec.confidence != null && (
												<Chip size="small" variant="outlined" label={`${rec.confidence}% sure`} />
											)}
											{!open && (
												<Chip
													size="small"
													color={rec.status === "APPLIED" ? "success" : "default"}
													label={rec.status === "APPLIED" ? "Applied" : "Dismissed"}
												/>
											)}
										</Stack>
									</Stack>
									<Typography variant="body2" color="text.secondary">
										{rec.reason}
									</Typography>
									{open && analysis.canApply && (
										<Stack direction="row" spacing={1} sx={{ mt: 1 }}>
											{applicable && (
												<Button
													size="small"
													variant="contained"
													startIcon={
														busyId === rec.id ? (
															<CircularProgress size={14} color="inherit" />
														) : (
															<Check />
														)
													}
													disabled={busyId != null}
													onClick={() => handleResolve(rec, true)}
												>
													Apply
												</Button>
											)}
											<Button
												size="small"
												startIcon={<Close />}
												disabled={busyId != null}
												onClick={() => handleResolve(rec, false)}
											>
												Dismiss
											</Button>
										</Stack>
									)}
								</Box>
							);
						})}
					</Stack>
					{analysis.recommendations?.some((r) => r.status === "APPLIED") && testId && (
						<Alert severity="success" sx={{ mt: 1.5 }}>
							The test was changed.{" "}
							<Link component={RouterLink} to={`/tests/${testId}`}>
								Open the test
							</Link>{" "}
							to review it, then run it again.
						</Alert>
					)}
					{!analysis.canApply &&
						analysis.recommendations?.some((r) => r.status === "OPEN" && r.action !== "ADVICE") && (
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
								Only the test's owner or an admin can apply these.
							</Typography>
						)}
				</>
			)}
		</Paper>
	);
}
